import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Typography,
  Grid2,
  Slider,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Tooltip,
  Box,
  CircularProgress, // Added CircularProgress for better loading indication
} from "@mui/material";
import { PhysioTreatmentMethods, painLevelValue } from "../Utility/Constants";
import { useSnackbar } from "../Utility/UserMessageContext";
import {
  addclinicalNote,
  updateClinicalNote,
  getClinicalNote,
  //viewClinicalNoteById // Commented out for now
} from "../API/ClinicalNoteApi";
import AuthContext from "../Context/AuthProvider";
import dayjs from "dayjs";
import { getServiceDetails } from "../API/LinkedServicesApi";
import { formatAge, showLoadingImage } from "../Utility/Utility";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import NotesTextField from "./NotesTextField.js";

function PhysiotherapyTreatmentNotes({ onSaveNote, serviceId, noteId }) {
  const [painLevel, setPainLevel] = useState(0);
  const [treatmentMethods, setTreatmentMethods] = useState(
    PhysioTreatmentMethods.map((method) => ({ ...method, selected: false })),
  );
  const [treatmentNotes, setTreatmentNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [noteStatus, setNoteStatus] = useState("Draft");
  const [currentNoteId, setCurrentNoteId] = useState(noteId);
  const [currentService, setCurrentService] = useState();
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const showLoading = isLoading || showSpinner;

  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null ? value : fallback;
  };
  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        setIsSaving(true); // Set loading state when fetching service details
        try {
          const result = await getServiceDetails(auth, serviceId);
          if (result?.isSuccess) {
            setCurrentService(result?.data);
          } else {
            showSnackbar(result?.data, "error");
          }
        } catch (error) {
          showSnackbar(
            "Error loading service details: " + error.message,
            "error",
          );
        } finally {
          // Only set loading to false if we're not also loading the note
          if (currentNoteId == null) {
            setIsSaving(false);
          }
        }
      }
    };

    const loadNote = async () => {
      if (auth != null && currentNoteId != null) {
        setIsSaving(true);
        try {
          const result = await getClinicalNote(auth, currentNoteId);
          if (result.isSuccess) {
            const noteData = JSON.parse(result?.data?.noteJson);
            setPainLevel(noteData.painLevel || 0);
            setTreatmentNotes(noteData.treatmentNotes || "");
            setNoteStatus(noteData.status || "Draft");

            // Update selected treatment methods
            const updatedMethods = PhysioTreatmentMethods.map((method) => {
              const isSelected = noteData.treatmentMethods?.some(
                (tm) => tm.value === method.value,
              );
              return { ...method, selected: isSelected || false };
            });
            setTreatmentMethods(updatedMethods);
          }
        } catch (error) {
          showSnackbar("Error loading note: " + error.message, "error");
        } finally {
          setIsSaving(false);
        }
      }
    };

    loadService();
    if (currentNoteId) {
      loadNote();
    }
  }, [currentNoteId, auth, serviceId]);

  const handleTreatmentMethodChange = (index) => {
    const newMethods = [...treatmentMethods];
    newMethods[index].selected = !newMethods[index].selected;
    setTreatmentMethods(newMethods);
  };

  let clinicalServiceCode = displayText(currentService?.serviceCode);
  let serviceName = displayText(currentService?.serviceName);

  let finalserviceName = clinicalServiceCode +" "+ serviceName;

  const handleSaveNote = async (status = "Draft") => {
    setIsSaving(true);

    const noteData = {
      painLevel,
      treatmentMethods: treatmentMethods.filter((method) => method.selected),
      treatmentNotes,
      status,
    };

    try {
      let result;
      if (!currentNoteId) {
        result = await addclinicalNote(
          auth,
          noteData,
          serviceId,
          "PhysiotherapyTreatment",
        );
        setCurrentNoteId(result?.data);
      } else {
        result = await updateClinicalNote(auth, noteData, currentNoteId);
      }

      if (result.isSuccess) {
        showSnackbar("Note saved successfully", "success");
        setNoteStatus(status);
        if (onSaveNote) onSaveNote(serviceId, currentNoteId, noteData.status);
      } else {
        showSnackbar("Failed to save note", "error");
      }
    } catch (error) {
      showSnackbar("Error saving note: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Loading Overlay using only isSaving state */}
      {isSaving && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1300,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            {showLoadingImage ? (
              showLoadingImage()
            ) : (
              <CircularProgress size={60} />
            )}
          </Box>
        </Box>
      )}

      <Grid2 container size={12}>
        {/* Note Actions */}
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar showSave={true} onSave={() => handleSaveNote("Draft")} />
          </Grid2>
        </Grid2>

        <Grid2
          size={{ sx: 12, sm: 12, md: 12, lg: 9, xl: 9 }}
          sx={{
            paddingTop: "10px",
            width: "100%",
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
          }}
        >
          <Card
            variant="outlined"
            sx={{
              width: "100%",
              padding: "10px",
              opacity: isSaving ? 0.7 : 1,
              transition: "opacity 0.3s ease",
            }}
          >
            <Grid2
              container
              spacing={3}
              size={12}
              sx={{ justifyContent: "center", paddingX: "10px" }}
            >
              <Grid2 size={12}>
                <Card
                  sx={{
                    padding: "16px",
                    pointerEvents: showLoading ? "none" : "auto",
                    opacity: showLoading ? 0.6 : 1,
                  }}
                  variant="outlined"
                >
                  <Grid2 size={12}>
                    <Grid2 container size={12}>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Patient Name"
                          value={
                            currentService?.patientPrefix +
                            " " +
                            currentService?.patientFirstName +
                            " " +
                            currentService?.patientLastName
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Age and Gender"
                          value={
                            formatAge(currentService?.patientAgeInYears) +
                            " and " +
                            currentService?.patientGender
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="MRN"
                          value={displayText(currentService?.patientMRN)}
                        />
                      </Grid2>
                    </Grid2>
                    <Grid2 container size={12}>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Visit Id"
                          value={displayText(currentService?.visitDisplayId)}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Visit Type"
                          value={displayText(currentService?.visitType)}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Visit Date Time"
                          value={convertUTCToTimeZoneDateTimeString(
                            new Date().toISOString(),
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                          )}
                        />
                      </Grid2>
                    </Grid2>
                    <Grid2 container size={12}>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Service Id"
                          value={displayText(currentService?.displayId)}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Service Name"
                          value={finalserviceName}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <HeaderValueDisplay
                          header="Service Date Time"
                          value={convertUTCToTimeZoneDateTimeString(
                            new Date().toISOString(),
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                          )}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Card>
              </Grid2>

              {/* Pain Level Section */}
              <Grid2 size={12}>
                <Typography>Pain Level (VAS)</Typography>
                <Grid2
                  container
                  size={12}
                  alignItems="center"
                  spacing={2}
                  justifyContent="center"
                >
                  <Grid2 size={10} md={8} lg={6}>
                    <Slider
                      value={painLevel}
                      step={1}
                      min={0}
                      max={10}
                      // valueLabelDisplay="auto"
                      onChange={(e) => setPainLevel(Number(e.target.value))}
                      sx={{ width: "100%" }}
                      disabled={isSaving || noteStatus === "Final"}
                      marks={painLevelValue}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Treatment Methods Section */}
              <Grid2 size={12}>
                <FormControl>
                  <FormLabel component="legend">Treatment Methods</FormLabel>
                  <FormGroup row>
                    {treatmentMethods?.map((treatment, index) => (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            checked={treatment.selected}
                            onChange={() => handleTreatmentMethodChange(index)}
                            disabled={isSaving || noteStatus === "Final"}
                          />
                        }
                        label={treatment.displayValue}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid2>

              {/* Treatment Notes Section */}
              <Grid2 size={12}>
                <NotesTextField
                  value={treatmentNotes}
                  onChange={setTreatmentNotes}
                  id="outlined-multiline-static"
                  label="Treatment Notes"
                  // value={treatmentNotes}
                  // onChange={(e) => setTreatmentNotes(e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                  slotProps={{
                    inputLabel: { shrink: treatmentNotes ? true : false },
                  }}
                  disabled={isSaving || noteStatus === "Final"}
                  maxLength={5000} // Assuming a max length for treatment notes
                />
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default PhysiotherapyTreatmentNotes;
